import { useEffect, useState } from "react";
import "./App.css";
import content from "./data/content.json";
import { ICONS } from "./icons";
import Admin from "./Admin";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaChevronDown,
} from "react-icons/fa";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const d = new Date();
  d.setHours(Number(h), Number(m), 0, 0);
  if (isNaN(d.getTime())) return t;
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function isUpcoming(event) {
  if (!event.date) return true;
  if (event.time) {
    const dt = new Date(`${event.date}T${event.time}:00`);
    if (!isNaN(dt.getTime())) return dt >= new Date();
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(event.date + "T00:00:00");
  return d >= today;
}

function TourRow({ event }) {
  return (
    <div className="tour-row">
      <div className="tour-info">
        <div className="tour-date">
          <FaCalendarAlt style={{ marginRight: "8px" }} />
          {formatDate(event.date)}
          {event.time && ` · ${formatTime(event.time)}`}
        </div>
        {event.place && (
          <a
            className="tour-place"
            href={
              event.mapsUrl?.trim() ||
              `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                event.place
              )}`
            }
            rel="noopener noreferrer"
            target="_blank"
          >
            <FaMapMarkerAlt style={{ marginRight: "8px" }} />
            {event.place}
          </a>
        )}
      </div>
    </div>
  );
}

const API = "/.netlify/functions/admin";

function PublicSite() {
  // Start empty and render the live content only once it has loaded, so we
  // never flash the bundled defaults before the real data arrives. The bundled
  // content is used only as a fallback if the fetch fails.
  const [data, setData] = useState(null);
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(API)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!active) return;
        if (d && Array.isArray(d.links) && Array.isArray(d.tourDates)) {
          setData(d);
        } else {
          setData(content);
        }
      })
      .catch(() => active && setData(content));
    return () => {
      active = false;
    };
  }, []);

  if (!data) {
    return (
      <div className="app-container">
        <div className="content-stack">
          <header className="site-header">
            <h1 className="site-name">Ivan Franchi</h1>
            <span className="site-accent" aria-hidden="true" />
          </header>
          <div className="spinner" role="status" aria-label="Loading">
            <span className="spinner-ring" />
          </div>
        </div>
      </div>
    );
  }

  const sortKey = (t) => `${t.date || ""}T${t.time || ""}`;
  const upcoming = data.tourDates
    .filter((t) => isUpcoming(t))
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
  const past = data.tourDates
    .filter((t) => !isUpcoming(t))
    .sort((a, b) => sortKey(b).localeCompare(sortKey(a)));

  return (
    <div className="app-container">
      <div className="content-stack">
        <header className="site-header">
          <h1 className="site-name">Ivan Franchi</h1>
          <span className="site-accent" aria-hidden="true" />
        </header>

        <div className="link-box">
          {data.links.map((link) => {
            const Icon = ICONS[link.icon] || ICONS.link;
            return (
              <a
                key={link.id}
                className="btn"
                href={link.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Icon style={{ marginRight: "10px" }} />
                {link.label}
              </a>
            );
          })}
        </div>

        {upcoming.length > 0 && (
          <div className="link-box tour-box">
            <h2 className="section-title">Upcoming Shows</h2>
            {upcoming.map((event) => (
              <TourRow key={event.id} event={event} />
            ))}
          </div>
        )}

        {past.length > 0 && (
          <div className="link-box tour-box">
            <button
              type="button"
              className="archive-toggle"
              onClick={() => setShowArchive((v) => !v)}
              aria-expanded={showArchive}
            >
              <span>Live show archive</span>
              <FaChevronDown className={`chevron ${showArchive ? "open" : ""}`} />
            </button>
            {showArchive &&
              past.map((event) => <TourRow key={event.id} event={event} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [route, setRoute] = useState(window.location.hash);

  useEffect(() => {
    const onHash = () => setRoute(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  if (route === "#admin") return <Admin />;
  return <PublicSite />;
}
